import { useState } from "react";
import { 
  Activity, 
  ChevronLeft, 
  ChevronRight,
  LayoutDashboard,
  Settings,
  Bell,
  BarChart3,
  Image,
  FileSearch,
  FileText,
  LayoutGrid,
  Gauge,
  Shield,
  AlertTriangle
} from "lucide-react";
import { cn } from "@/lib/utils";

const navItems = [
  { id: "uptime", label: "Uptime Monitoring", icon: Activity },
  { id: "images", label: "Image Monitoring", icon: Image },
  { id: "seo", label: "MetaAlt Monitoring", icon: FileSearch },
  { id: "metaalt-quality", label: "MetaAlt Quality", icon: AlertTriangle },
  { id: "onpage", label: "OnPage Monitoring", icon: FileText },
  { id: "sections", label: "Section Monitoring", icon: LayoutGrid },
  { id: "pagespeed", label: "PageSpeed Monitoring", icon: Gauge },
  { id: "techseo", label: "TechSEO Monitoring", icon: Shield },
  { id: "analytics", label: "GA4 Analytics", icon: BarChart3 },
  { id: "alerts", label: "Alert Rules", icon: Bell },
  { id: "settings", label: "Settings", icon: Settings },
];

export const Sidebar = ({ isCollapsed, setIsCollapsed, activeTab, setActiveTab }) => {
  return (
    <aside 
      className={cn(
        "fixed left-0 top-0 h-screen bg-[#0c0c0e] border-r border-zinc-800 transition-all duration-300 z-50 flex flex-col",
        isCollapsed ? "w-16" : "w-56"
      )}
      data-testid="sidebar"
    >
      {/* Logo */}
      <div className="h-16 flex items-center justify-between px-4 border-b border-zinc-800">
        {!isCollapsed && (
          <div className="flex items-center gap-2">
            <LayoutDashboard className="w-6 h-6 text-green-500" />
            <span className="font-mono font-bold text-sm text-white">SEO Monitor 360</span>
          </div>
        )}
        {isCollapsed && (
          <LayoutDashboard className="w-6 h-6 text-green-500 mx-auto" />
        )}
      </div>

      {/* Navigation */}
      <nav className="flex-1 py-4 px-2">
        <ul className="space-y-1">
          {navItems.map((item) => (
            <li key={item.id}>
              <button
                onClick={() => !item.disabled && setActiveTab(item.id)}
                disabled={item.disabled}
                className={cn(
                  "w-full flex items-center gap-3 px-3 py-2.5 rounded-sm transition-colors font-mono text-xs",
                  activeTab === item.id 
                    ? "bg-zinc-800 text-white" 
                    : item.disabled 
                      ? "text-zinc-600 cursor-not-allowed" 
                      : "text-zinc-400 hover:bg-zinc-800/50 hover:text-white",
                  isCollapsed && "justify-center px-2"
                )}
                data-testid={`nav-${item.id}`}
                title={isCollapsed ? item.label : undefined}
              >
                <item.icon className={cn(
                  "w-5 h-5 flex-shrink-0",
                  activeTab === item.id ? "text-green-500" : ""
                )} />
                {!isCollapsed && (
                  <span className="flex-1 text-left">{item.label}</span>
                )}
                {!isCollapsed && item.coming && (
                  <span className="text-[10px] px-1.5 py-0.5 bg-zinc-700 text-zinc-400 rounded">
                    SOON
                  </span>
                )}
              </button>
            </li>
          ))}
        </ul>
      </nav>

      {/* Collapse Toggle */}
      <div className="p-2 border-t border-zinc-800">
        <button
          onClick={() => setIsCollapsed(!isCollapsed)}
          className="w-full flex items-center justify-center gap-2 px-3 py-2 text-zinc-500 hover:text-white hover:bg-zinc-800/50 rounded-sm transition-colors"
          data-testid="sidebar-toggle"
        >
          {isCollapsed ? (
            <ChevronRight className="w-5 h-5" />
          ) : (
            <>
              <ChevronLeft className="w-5 h-5" />
              <span className="font-mono text-xs">Collapse</span>
            </>
          )}
        </button>
      </div>
    </aside>
  );
};

export default Sidebar;
